import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable/AppTable";
import CustomTabs from "components/Shared/CustomTabs";

import { useLazyListEntitiesQuery } from "store/services/entityApi";

import { useAuth, useAppMeta } from "hooks";

const EntityListScreen = () => {
  const [activeTab, setActiveTab] = React.useState("Active");

  const [searchParams] = useSearchParams();

  const breadcrumbs = [
    {
      title: "Entities",
    },
  ];

  const { viewEntity, isSystemAdmin } = useAuth();

  const entityListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  // RTK Query
  const [
    fetchEntities,
    {
      data: entities,
      isLoading: loadingEntities,
      isFetching: fetchingEntities,
      error,
    },
  ] = useLazyListEntitiesQuery();

  React.useEffect(() => {
    setActiveTab(searchParams.get("tab") ?? "Active");
  }, [searchParams]);

  React.useEffect(() => {
    if (!viewEntity) return;
    fetchEntities({
      active: activeTab === "Active",
      ...(!isSystemAdmin && { account: viewEntity?.entity?.account?._id?._id }),
    });
  }, [activeTab, fetchEntities, isSystemAdmin, viewEntity]);

  useAppMeta("Entities");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={entityListTabs} activeTab={activeTab} />

        <AppTable
          columns={columns}
          data={entities}
          progressPending={loadingEntities || fetchingEntities}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default EntityListScreen;

const columns = [
  {
    name: "Name",
    selector: (row) => row?.name,
    sortable: true,
    cell: (row) => (
      <Link
        to={`/app/crm/entity/${row?._id}/detail`}
        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        title={row?.name}
      >
        {row?.name ?? "N/A"}
      </Link>
    ),
  },
  { name: "Category", selector: (row) => row?.category },
  {
    name: "Currency",
    selector: (row) => row?.currency?.name,
  },
  {
    name: "Actions",
    cell: (row) => (
      <Box display="flex" alignItems="center">
        <Link to={`/app/crm/entity/${row?._id}/detail`}>
          <IconButton size="small" aria-label="Entity">
            <InfoOutlined color="primary" />
          </IconButton>
        </Link>

        <Link to={`/app/crm/entity/${row?._id}/edit`}>
          <IconButton size="small" aria-label="Edit Entity">
            <ModeEditOutlineTwoTone />
          </IconButton>
        </Link>
      </Box>
    ),
    ignoreRowClick: true,
    right: true,
  },
];

const PageActions = () => {
  return (
    <Link to="/app/crm/entity/create">
      <Button startIcon={<Add />} variant="contained">
        New Entity
      </Button>
    </Link>
  );
};
